import { useState } from "react";
import { toast } from "sonner";
import { Mail, Copy, Check, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import { CONTACT_EMAIL, COMPANY } from "@/config/site";
import { usePageMeta } from "@/lib/meta";

const Contact = () => {
  usePageMeta(
    "Contact Us | CoachER",
    "Questions about CoachER — pricing, teams, or the app launch? Reach the team directly by email.",
    "/contact"
  );
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      toast.success("Email address copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — the address is below.");
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden bg-coacher-navy pt-32 pb-20 sm:pt-44 sm:pb-28">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/70">
            <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
            Contact Us
          </p>
          <MaskedLines
            lines={["Talk to a real", "coach on our team."]}
            delay={0.15}
            className="mt-6 max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-semibold uppercase leading-[1.0] tracking-tight text-white"
            lineClassName="[&:nth-child(2)]:text-outline-white"
          />
        </div>
      </section>

      <Marquee items={["We read every email", "Built from the dugout", "Ohio, USA"]} />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <Reveal>
            <div data-testid="contact-email-card" className="rounded-xl border border-coacher-border bg-coacher-surface p-8 sm:p-12 text-center shadow-card">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-coacher-navy">
                <Mail size={24} className="text-white" />
              </span>
              <h2 className="mt-7 font-display text-2xl sm:text-3xl font-semibold uppercase tracking-tight text-coacher-navy">
                One inbox. Straight to us.
              </h2>
              <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-coacher-slate">
                Questions about pricing, teams, features, or the app launch — send them over. A real person on the CoachER team reads every message.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="mt-8 inline-block font-display text-2xl sm:text-4xl font-semibold tracking-tight text-coacher-navy underline decoration-coacher-red decoration-4 underline-offset-8 transition-colors hover:text-coacher-red break-all"
              >
                {CONTACT_EMAIL}
              </a>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={copyEmail}
                  data-testid="contact-copy-email-button"
                  className="inline-flex items-center gap-2 rounded-md bg-coacher-navy px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-coacher-navy-deep"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy Email"}
                </button>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=CoachER%20Question`}
                  data-testid="contact-mailto-button"
                  className="inline-flex items-center gap-2 rounded-md border-2 border-coacher-navy px-6 py-3 text-sm font-semibold text-coacher-navy transition-colors duration-200 hover:bg-coacher-navy hover:text-white"
                >
                  Open Mail App <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="mt-10">
            <div className="rounded-xl border border-coacher-border bg-white p-7 shadow-card">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-coacher-red">Helpful to include</p>
              <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-coacher-slate">
                <li className="flex items-start gap-3"><span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-coacher-red" />What you're coaching — age group, baseball or softball</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-coacher-red" />Your role — head coach, assistant, or league organizer</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-coacher-red" />Anything that's slowing your season down</li>
              </ul>
              <p className="mt-6 text-xs text-coacher-slate">
                CoachER is developed by {COMPANY.name}, {COMPANY.location}.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
